import { cn } from "@/lib/utils";
import {
    AnimationStart,
    AnimationVariant,
    useAnimatedThemeToggle
} from "hooks/useAnimatedThemeToggle";

import { IconBox } from "./IconBox";

type ThemeToggleProps = {
    size?: "small" | "medium" | "large";
    className?: string;
    variant?: AnimationVariant;
    animationStart?: AnimationStart;
    blur?: boolean;
    duration?: string;
};

export function ThemeToggle({
    size = "medium",
    className,
    variant = "rectangle",
    animationStart = "top-down",
    blur = false,
    duration = "0.4s"
}: ThemeToggleProps) {
    const { isDark, toggleTheme } = useAnimatedThemeToggle({
        variant,
        start: animationStart,
        blur,
        duration
    });

    const boxDims = {
        small: { side: 32, radius: 6 },
        medium: { side: 40, radius: 8 },
        large: { side: 48, radius: 10 }
    }[size];
    const iconPx = Math.round(boxDims.side * 0.55);

    return (
        <button
            onClick={toggleTheme}
            className={cn(
                `relative flex h-8 w-8 cursor-pointer items-center
                justify-center border-none`,
                `transition-[background-color,box-shadow,transform] duration-200
                ease-in-out`,
                "hover:bg-[rgba(255,255,255,0.16)]",
                "active:translate-y-px",
                `focus-visible:outline focus-visible:outline-2
                focus-visible:outline-offset-2
                focus-visible:outline-[rgba(99,143,255,0.6)]`,
                className
            )}
            style={{
                borderRadius: `${boxDims.radius}px`
            }}
            aria-label={`Switch to ${isDark ? "light" : "dark"} mode`}
            title={`Switch to ${isDark ? "light" : "dark"} mode`}
        >
            {isDark ? (
                <IconBox color="text-grey-500" size="small" name="Moon" />
            ) : (
                <IconBox color="text-grey-500" size="medium" name="Sun" />
            )}
        </button>
    );
}
